import { ChangeEvent, useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Image from '@cartwhl/ui/atoms/Image';
import Input from '@cartwhl/ui/atoms/Input';
import Text from '@cartwhl/ui/base/Text';
import DownloadIcon from '@cartwhl/ui/icons/DownloadIcon';
import DownvoteIcon from '@cartwhl/ui/icons/DownvoteIcon';
import HistoryIcon from '@cartwhl/ui/icons/HistoryIcon';
import ThreeDotsIcon from '@cartwhl/ui/icons/ThreeDotsIcon';
import UpvoteIcon from '@cartwhl/ui/icons/UpvoteIcon';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import { noticesStore, posingStore, projectStore, uiStore } from 'stores';

import RenderFeedbackModal from './RenderFeedbackModal';

export default function RenderViewModal({
  open,
  onOpenChange,
  renderedPoseURL,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  renderedPoseURL: string;
}) {
  const { lastRenderPrompt, posingSystem, renderingInProgress } = useSnapshot(
    posingStore.state,
  );
  const [prompt, setPrompt] = useState(lastRenderPrompt || '');
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackSentiment, setFeedbackSentiment] = useState<
    'positive' | 'negative'
  >('positive');

  useEffect(() => {
    if ((lastRenderPrompt || '') !== prompt) {
      setPrompt(lastRenderPrompt || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRenderPrompt]);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = renderedPoseURL;
    link.download = 'rendered-pose.png';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.dispatchEvent(new MouseEvent('click'));
  };

  const openFeedback = (sentiment: 'positive' | 'negative') => {
    setFeedbackSentiment(sentiment);
    setIsFeedbackOpen(true);
  };

  const handleChangePrompt = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setPrompt(value);
    posingStore.state.lastRenderPrompt = value;
  };

  const handleRender = async () => {
    if (!posingSystem.current || !projectStore.state.currentProject) {
      noticesStore.info(
        'An error has occurred, please refresh the page and try again.',
      );
      return;
    }

    const canvasImageTaskURL = await posingSystem.current.canvasImageTask({
      showAxelClothes: true,
      // let's keep playing with this
      // showDome: true,
      // showGrid: true,
    });
    const canvasBase64 = canvasImageTaskURL.replace(
      /^data:image\/\w+;base64,/,
      '',
    );

    posingStore.setLastCanvasBase64(canvasBase64);

    if (!projectStore.state.currentProject.data?.projectID) {
      noticesStore.info(
        'An error has occurred, please refresh the page and try again.',
      );
      return;
    }

    await posingStore.renderPose(
      projectStore.state.currentProject.data.projectID,
      posingStore.state.lastRenderPrompt,
      canvasBase64,
    );
  };

  const titleDiv = (
    <div className="flex w-full items-center">
      <Text as="h2" variant="studio/title/xl">
        Render view
      </Text>

      <div className="ml-auto flex items-center gap-2">
        <Button
          icon={<DownloadIcon />}
          size="sm"
          variant="secondary"
          onClick={handleDownload}
        >
          Download image
        </Button>
        <Dropdown
          trigger={
            <Button size="sm" variant="secondary" icon={<ThreeDotsIcon />} />
          }
        >
          <Dropdown.Item onClick={() => uiStore.setOpenPanel('history')}>
            <div className="flex items-center gap-2">
              <HistoryIcon /> Render history
            </div>
          </Dropdown.Item>
          <Dropdown.Separator />
          <Dropdown.Item onClick={() => openFeedback('positive')}>
            <div className="flex items-center gap-2">
              <UpvoteIcon /> Report great
            </div>
          </Dropdown.Item>
          <Dropdown.Item onClick={() => openFeedback('negative')}>
            <div className="flex items-center gap-2">
              <DownvoteIcon /> Report issue
            </div>
          </Dropdown.Item>
        </Dropdown>
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={titleDiv}
      maxWidth={1138}
      closeOnClickOutside={true}
    >
      <div className="flex flex-col gap-2">
        <div className="flex justify-center">
          <Image
            src={renderedPoseURL}
            crossOrigin="anonymous"
            alt="Rendered pose"
            className="h-auto max-h-[70vh] w-auto max-w-full rounded-lg object-contain"
          />
        </div>
        <div className="relative">
          <Input
            name="render-modal"
            as="textarea"
            placeholder="Describe what you want the character and scene to look like..."
            value={prompt}
            onChange={handleChangePrompt}
            className="h-[114px]"
          />
          <Button
            variant="primary"
            size="sm"
            loading={renderingInProgress}
            onClick={handleRender}
            className="absolute right-2 bottom-4"
          >
            Render
          </Button>
        </div>
        <div className="flex justify-between">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          <Button
            size="sm"
            variant="secondary"
            icon={<HistoryIcon />}
            onClick={() => {
              uiStore.setOpenPanel('history');
              onOpenChange(false);
            }}
          >
            Render history
          </Button>
        </div>
      </div>

      {isFeedbackOpen && (
        <RenderFeedbackModal
          open={isFeedbackOpen}
          onOpenChange={setIsFeedbackOpen}
          sentiment={feedbackSentiment}
          prompt={lastRenderPrompt}
          imageURL={renderedPoseURL}
        />
      )}
    </Modal>
  );
}
